/*
 * Earbuds Restore foundation changelog:
 * - v0.1.3: add an idempotent offline-status host and force every product gate back to
 *   UNKNOWN with confirmation cleared whenever the browser reports no connection.
 * - v0.1.2: validate the production adapter envelope, product/price binding, active evidence,
 *   strict booleans and freshness; harden submit ordering and native variant state.
 * - v0.1.1: reject near-sentinels/duplicate bindings, validate evidence dates and identifiers,
 *   and name the current target plus sellable unit in the explicit confirmation.
 * - v0.1.0: add the fail-closed compatibility and purchase gate.
 */

export const ADAPTER_CONTRACT_VERSION = 'er-product-truth-adapter/0.1';
export const PRODUCT_TRUTH_CONTRACT_VERSION = 'earbuds-restore-product-truth/0.1';

const SHA256_PATTERN = /^[a-f0-9]{64}$/i;

// B4: the owner approved checkout from the Netherlands, Belgium and France on
// 10 September 2026. One list, spelled identically everywhere a country is
// decided, so a later edit cannot open one route and leave another closed.
const ALLOWED_COUNTRIES = Object.freeze(['NL', 'BE', 'FR']);

export const SHOPPER_STATES = Object.freeze({
  MATCH: 'MATCH',
  NO_MATCH: 'NO_MATCH',
  UNKNOWN: 'UNKNOWN',
});

const PART_TYPES = new Set(['earbud_L', 'earbud_R', 'case', 'set']);
const SIDES = new Set(['left', 'right', 'pair', 'NOT_APPLICABLE']);
const PENDING_VALUES = new Set([
  '',
  '?',
  'N/A',
  'N.A.',
  'NA',
  'NONE',
  'NOT_APPLICABLE',
  'NVT',
  'N.V.T.',
  'OWNER_REQUIRED',
  'PENDING',
  'TBD',
  'TO BE DETERMINED',
  'UNKNOWN',
  'UNRESOLVED',
]);

const MATERIAL_SELECTION_SELECTOR = [
  '[data-er-part]',
  '[data-er-family]',
  '[data-er-generation]',
  '[data-er-model]',
  '[data-er-side]',
  '[data-er-case]',
  '[data-er-connector]',
  '[data-er-condition]',
  '[data-er-material-selection]',
].join(', ');

const MATERIAL_CONTEXT_ATTRIBUTES = [
  'data-contract',
  'data-country-code',
  'data-currency-code',
  'data-selected-variant-id',
  'data-selected-variant-price',
];

function text(value) {
  return value === null || value === undefined ? '' : String(value).trim();
}

function isConcrete(value) {
  const normalized = text(value).normalize('NFKC').replace(/\s+/g, ' ').toUpperCase();
  return !PENDING_VALUES.has(normalized);
}

function isValidIsoDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text(value));
  if (!match) return false;

  const [, year, month, day] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function parseIsoInstant(value) {
  const raw = text(value);
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(raw)) return null;
  const timestamp = Date.parse(raw);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function containsSyntheticMarker(value) {
  if (typeof value === 'string') return /^(?:SYNTH(?:ETIC)?|TEST)[_:-]/i.test(value.trim());
  if (Array.isArray(value)) return value.some(containsSyntheticMarker);
  if (value && typeof value === 'object') return Object.values(value).some(containsSyntheticMarker);
  return false;
}

function validateAdapterEnvelope(contract, input, evaluationContext) {
  const errors = [];
  const now = parseIsoInstant(evaluationContext.now || new Date().toISOString());
  const generatedAt = parseIsoInstant(contract?.adapter_generated_at);
  const expiresAt = parseIsoInstant(contract?.adapter_expires_at);
  const environment = text(contract?.environment);

  if (text(contract?.truth_contract_version) !== PRODUCT_TRUTH_CONTRACT_VERSION) {
    errors.push('TRUTH_CONTRACT_VERSION_UNSUPPORTED');
  }
  if (!SHA256_PATTERN.test(text(contract?.validated_dataset_sha256))) errors.push('DATASET_HASH_INVALID');
  if (!isConcrete(contract?.adapter_id)) errors.push('ADAPTER_ID_UNRESOLVED');
  if (!isConcrete(contract?.source_validator_version)) errors.push('SOURCE_VALIDATOR_VERSION_UNRESOLVED');
  if (contract?.source_validation_status !== 'PASS') errors.push('SOURCE_VALIDATION_NOT_PASSING');
  if (contract?.source_validator_errors !== 0) errors.push('SOURCE_VALIDATOR_ERRORS_PRESENT');
  if (contract?.source_validator_warnings !== 0) errors.push('SOURCE_VALIDATOR_WARNINGS_PRESENT');
  if (contract?.dataset_active !== true) errors.push('DATASET_NOT_ACTIVE');
  if (contract?.adapter_superseded !== false) errors.push('ADAPTER_SUPERSESSION_UNRESOLVED');
  if (generatedAt === null) errors.push('ADAPTER_GENERATED_AT_INVALID');
  if (expiresAt === null) errors.push('ADAPTER_EXPIRES_AT_INVALID');
  if (generatedAt !== null && now !== null && generatedAt > now) errors.push('ADAPTER_GENERATED_IN_FUTURE');
  if (expiresAt !== null && now !== null && expiresAt <= now) errors.push('ADAPTER_EXPIRED');
  if (generatedAt !== null && expiresAt !== null && expiresAt <= generatedAt) {
    errors.push('ADAPTER_VALIDITY_WINDOW_INVALID');
  }
  if (!['production', 'test'].includes(environment)) errors.push('ADAPTER_ENVIRONMENT_INVALID');
  if (typeof contract?.synthetic !== 'boolean') errors.push('SYNTHETIC_MARKER_INVALID');
  if (environment === 'production' && contract?.synthetic !== false) errors.push('PRODUCTION_ADAPTER_SYNTHETIC');
  if (environment === 'test' && contract?.synthetic !== true) errors.push('TEST_ADAPTER_MARKER_INVALID');
  if ((contract?.synthetic === true || containsSyntheticMarker(contract)) && evaluationContext.allowSynthetic !== true) {
    errors.push('SYNTHETIC_CONTENT_FORBIDDEN');
  }
  if (!isConcrete(contract?.shopify_product_id)) errors.push('PRODUCT_BINDING_UNRESOLVED');
  if (text(contract?.shopify_product_id) !== input.shopifyProductId) errors.push('PRODUCT_BINDING_MISMATCH');
  if (!isConcrete(contract?.product_binding_ref)) errors.push('PRODUCT_BINDING_EVIDENCE_UNRESOLVED');
  if (!isConcrete(contract?.projection_mapping_version)) errors.push('PROJECTION_MAPPING_VERSION_UNRESOLVED');
  if (!SHA256_PATTERN.test(text(contract?.projection_mapping_sha256))) {
    errors.push('PROJECTION_MAPPING_HASH_INVALID');
  }
  if (contract?.projection_mapping_active !== true) errors.push('PROJECTION_MAPPING_NOT_ACTIVE');

  return [...new Set(errors)];
}

function unknown(reason, errors = [], details = {}) {
  return {
    state: SHOPPER_STATES.UNKNOWN,
    reason,
    errors,
    ruleId: '',
    policyBlock: false,
    allOtherPurchaseGatesPass: false,
    variantAvailable: false,
    contractVersion: '',
    ...details,
  };
}

function validateVariantProjection(variant, contract) {
  const errors = [];
  const partType = text(variant?.part_type);
  const side = text(variant?.side);

  if (!isConcrete(variant?.variant_id)) errors.push('VARIANT_ID_UNRESOLVED');
  if (!isConcrete(variant?.shopify_variant_id)) errors.push('SHOPIFY_VARIANT_ID_UNRESOLVED');
  if (!isConcrete(variant?.component_type_code)) errors.push('COMPONENT_TYPE_UNRESOLVED');
  if (!isConcrete(variant?.component_registration_ref)) errors.push('COMPONENT_REGISTRATION_UNRESOLVED');
  if (!isConcrete(variant?.component_mapping_ref)) errors.push('COMPONENT_MAPPING_EVIDENCE_UNRESOLVED');
  if (!isConcrete(variant?.component_mapping_version)) errors.push('COMPONENT_MAPPING_VERSION_UNRESOLVED');
  if (text(variant?.component_mapping_version) !== text(contract?.projection_mapping_version)) {
    errors.push('COMPONENT_MAPPING_VERSION_MISMATCH');
  }
  if (variant?.component_registration_active !== true) errors.push('COMPONENT_REGISTRATION_NOT_ACTIVE');
  if (variant?.component_mapping_active !== true) errors.push('COMPONENT_MAPPING_NOT_ACTIVE');
  if (variant?.binding_active !== true) errors.push('VARIANT_BINDING_NOT_ACTIVE');
  if (variant?.binding_superseded !== false) errors.push('VARIANT_BINDING_SUPERSESSION_UNRESOLVED');
  if (text(variant?.shopify_product_id) !== text(contract?.shopify_product_id)) {
    errors.push('VARIANT_PRODUCT_BINDING_MISMATCH');
  }
  if (!Number.isInteger(variant?.approved_price_minor) || variant.approved_price_minor < 0) {
    errors.push('APPROVED_PRICE_INVALID');
  }
  if (!/^[A-Z]{3}$/.test(text(variant?.currency_code))) errors.push('CURRENCY_CODE_INVALID');
  for (const field of ['available', 'finder_eligible', 'all_other_purchase_gates_pass', 'policy_block']) {
    if (typeof variant?.[field] !== 'boolean') errors.push(`${field.toUpperCase()}_BOOLEAN_INVALID`);
  }
  if (!PART_TYPES.has(partType)) errors.push('PART_TYPE_UNRESOLVED');
  if (!SIDES.has(side)) errors.push('SIDE_UNRESOLVED');
  if (variant?.projection_source !== 'versioned_component_mapping') errors.push('PROJECTION_SOURCE_INVALID');

  if (partType === 'earbud_L') {
    if (variant?.component_sided !== true || side !== 'left') errors.push('LEFT_PROJECTION_CONFLICT');
    if (!isConcrete(variant?.side_verification_ref)) errors.push('SIDE_EVIDENCE_UNRESOLVED');
  }

  if (partType === 'earbud_R') {
    if (variant?.component_sided !== true || side !== 'right') errors.push('RIGHT_PROJECTION_CONFLICT');
    if (!isConcrete(variant?.side_verification_ref)) errors.push('SIDE_EVIDENCE_UNRESOLVED');
  }

  if (partType === 'case' || partType === 'set') {
    if (variant?.component_sided !== false || side !== 'NOT_APPLICABLE') {
      errors.push('NON_SIDED_PROJECTION_CONFLICT');
    }
    if (text(variant?.side_verification_ref) !== 'NOT_APPLICABLE') {
      errors.push('NON_SIDED_EVIDENCE_CONFLICT');
    }
  }

  if (partType === 'set' && side === 'pair') errors.push('SET_MUST_NOT_COERCE_TO_PAIR');

  return errors;
}

function validateProductProjection(contract) {
  const variants = Array.isArray(contract?.variants) ? contract.variants : [];
  const productPartType = text(contract?.product_part_type);
  const errors = [];

  if (!PART_TYPES.has(productPartType)) errors.push('PRODUCT_PART_TYPE_UNRESOLVED');
  if (variants.length === 0) errors.push('VARIANTS_ABSENT');

  const projectedPartTypes = new Set();
  const variantIds = new Set();
  const shopifyVariantIds = new Set();
  for (const variant of variants) {
    const partType = text(variant?.part_type);
    if (PART_TYPES.has(partType)) projectedPartTypes.add(partType);
    errors.push(...validateVariantProjection(variant, contract));

    const variantId = text(variant?.variant_id);
    const shopifyVariantId = text(variant?.shopify_variant_id);
    if (variantIds.has(variantId)) errors.push('DUPLICATE_VARIANT_BINDING');
    if (shopifyVariantIds.has(shopifyVariantId)) errors.push('DUPLICATE_SHOPIFY_VARIANT_BINDING');
    variantIds.add(variantId);
    shopifyVariantIds.add(shopifyVariantId);
  }

  if (projectedPartTypes.size !== 1 || !projectedPartTypes.has(productPartType)) {
    errors.push('MIXED_OR_CONFLICTING_PRODUCT_PROJECTION');
  }

  return [...new Set(errors)];
}

function exactTarget(row, input) {
  return (
    text(row?.target_model_code) === input.targetModelCode &&
    text(row?.target_generation_code) === input.targetGenerationCode &&
    text(row?.target_side) === input.targetSide
  );
}

function rowEvidenceErrors(row) {
  const errors = [];

  if (!isConcrete(row?.compat_id)) errors.push('COMPATIBILITY_ID_UNRESOLVED');
  if (row?.finder_visible !== true) errors.push('ROW_NOT_FINDER_VISIBLE');
  if (!isConcrete(row?.verification_method_code)) errors.push('VERIFICATION_METHOD_UNRESOLVED');
  if (!isConcrete(row?.verification_method_registration_ref)) {
    errors.push('VERIFICATION_METHOD_REGISTRATION_UNRESOLVED');
  }
  if (!isConcrete(row?.verification_ref)) errors.push('VERIFICATION_REF_UNRESOLVED');
  if (!isValidIsoDate(row?.verified_at)) errors.push('VERIFIED_AT_INVALID');
  if (row?.verification_active !== true) errors.push('VERIFICATION_NOT_ACTIVE');
  if (row?.verification_superseded !== false) errors.push('VERIFICATION_SUPERSESSION_UNRESOLVED');
  if (row?.verification_method_active !== true) errors.push('VERIFICATION_METHOD_NOT_ACTIVE');
  if (row?.target_vocabulary_active !== true) errors.push('TARGET_VOCABULARY_NOT_ACTIVE');
  if (!isConcrete(row?.target_model_registration_ref)) errors.push('TARGET_MODEL_REGISTRATION_UNRESOLVED');
  if (!isConcrete(row?.target_generation_registration_ref)) {
    errors.push('TARGET_GENERATION_REGISTRATION_UNRESOLVED');
  }
  if (typeof row?.finder_visible !== 'boolean') errors.push('FINDER_VISIBLE_BOOLEAN_INVALID');
  if (typeof row?.policy_block !== 'boolean') errors.push('POLICY_BLOCK_BOOLEAN_INVALID');

  const severity = text(row?.compat_severity);
  if (severity && !['info', 'warning', 'block'].includes(severity)) errors.push('COMPAT_SEVERITY_INVALID');
  if (severity === 'block') {
    if (row?.policy_block !== true) errors.push('BLOCK_SEVERITY_REQUIRES_POLICY_BLOCK');
    if (!isConcrete(row?.policy_rule_id)) errors.push('POLICY_RULE_ID_UNRESOLVED');
    if (!isConcrete(row?.policy_reason)) errors.push('POLICY_REASON_UNRESOLVED');
    if (!isConcrete(row?.policy_provenance_ref)) errors.push('POLICY_PROVENANCE_UNRESOLVED');
  }

  if (row?.policy_block === true) {
    if (!isConcrete(row?.policy_rule_id)) errors.push('POLICY_RULE_ID_UNRESOLVED');
    if (!isConcrete(row?.policy_reason)) errors.push('POLICY_REASON_UNRESOLVED');
    if (!isConcrete(row?.policy_provenance_ref)) errors.push('POLICY_PROVENANCE_UNRESOLVED');
  }

  return [...new Set(errors)];
}

export function evaluateCompatibility(contract, rawInput = {}, evaluationContext = {}) {
  if (!contract || typeof contract !== 'object' || Array.isArray(contract)) return unknown('CONTRACT_ABSENT');
  if (text(contract.contract_version) !== ADAPTER_CONTRACT_VERSION) return unknown('CONTRACT_VERSION_UNSUPPORTED');

  const input = {
    shopifyProductId: text(rawInput.shopifyProductId),
    shopifyVariantId: text(rawInput.shopifyVariantId),
    shopifyPriceMinor: Number(rawInput.shopifyPriceMinor),
    currencyCode: text(rawInput.currencyCode).toUpperCase(),
    requestedPartType: text(rawInput.requestedPartType),
    targetModelCode: text(rawInput.targetModelCode),
    targetGenerationCode: text(rawInput.targetGenerationCode),
    targetSide: text(rawInput.targetSide),
  };

  const envelopeErrors = validateAdapterEnvelope(contract, input, evaluationContext);
  if (envelopeErrors.length > 0) {
    return unknown('ADAPTER_ENVELOPE_INVALID', envelopeErrors, {
      contractVersion: text(contract.contract_version),
    });
  }

  const projectionErrors = validateProductProjection(contract);
  if (projectionErrors.length > 0) {
    return unknown('PRODUCT_PROJECTION_INVALID', projectionErrors, {
      contractVersion: text(contract.contract_version),
    });
  }

  if (
    !isConcrete(input.shopifyProductId) ||
    !isConcrete(input.shopifyVariantId) ||
    !Number.isInteger(input.shopifyPriceMinor) ||
    input.shopifyPriceMinor < 0 ||
    !/^[A-Z]{3}$/.test(input.currencyCode) ||
    !PART_TYPES.has(input.requestedPartType) ||
    !isConcrete(input.targetModelCode) ||
    !isConcrete(input.targetGenerationCode) ||
    !SIDES.has(input.targetSide)
  ) {
    return unknown('SHOPPER_INPUT_UNRESOLVED', [], {
      contractVersion: text(contract.contract_version),
    });
  }

  const variants = contract.variants.filter(
    (variant) => text(variant?.shopify_variant_id) === input.shopifyVariantId,
  );

  if (variants.length !== 1) {
    return unknown('SELECTED_VARIANT_UNRESOLVED', ['SELECTED_VARIANT_CARDINALITY'], {
      contractVersion: text(contract.contract_version),
    });
  }

  const variant = variants[0];
  if (
    variant.approved_price_minor !== input.shopifyPriceMinor ||
    text(variant.currency_code) !== input.currencyCode
  ) {
    return unknown('SHOPIFY_PRICE_BINDING_MISMATCH', ['PRICE_OR_CURRENCY_MISMATCH'], {
      contractVersion: text(contract.contract_version),
      variantId: text(variant.variant_id),
      shopifyVariantId: text(variant.shopify_variant_id),
    });
  }

  const baseDetails = {
    contractVersion: text(contract.contract_version),
    truthContractVersion: text(contract.truth_contract_version),
    datasetSha256: text(contract.validated_dataset_sha256).toLowerCase(),
    adapterExpiresAt: text(contract.adapter_expires_at),
    shopifyProductId: text(contract.shopify_product_id),
    variantId: text(variant.variant_id),
    shopifyVariantId: text(variant.shopify_variant_id),
    approvedPriceMinor: variant.approved_price_minor,
    currencyCode: text(variant.currency_code),
    variantAvailable: variant.available === true,
    allOtherPurchaseGatesPass: variant.all_other_purchase_gates_pass === true,
    policyBlock: variant.policy_block === true,
  };

  if (text(variant.part_type) !== input.requestedPartType) {
    return {
      ...unknown('PART_TYPE_MISMATCH', [], baseDetails),
      state: SHOPPER_STATES.NO_MATCH,
    };
  }

  if (variant.finder_eligible !== true) return unknown('FINDER_GATE_BLOCKED', [], baseDetails);

  const rows = Array.isArray(contract.compatibility) ? contract.compatibility : [];
  const exactRows = rows.filter(
    (row) => text(row?.variant_id) === text(variant.variant_id) && exactTarget(row, input),
  );

  if (exactRows.length === 0) return unknown('EXACT_COMPATIBILITY_ROW_ABSENT', [], baseDetails);
  if (exactRows.length !== 1) {
    return unknown('EXACT_COMPATIBILITY_CONFLICT', ['COMPATIBILITY_CARDINALITY'], baseDetails);
  }

  const row = exactRows[0];
  const evidenceErrors = rowEvidenceErrors(row);
  if (evidenceErrors.length > 0) return unknown('COMPATIBILITY_EVIDENCE_UNRESOLVED', evidenceErrors, baseDetails);

  const compatibilityState = text(row.compat_state);
  const policyBlock = baseDetails.policyBlock || row.policy_block === true;
  const details = {
    ...baseDetails,
    ruleId: text(row.compat_id),
    policyBlock,
  };

  if (compatibilityState === 'compatible') {
    return {
      state: SHOPPER_STATES.MATCH,
      reason: 'EXACT_EVIDENCED_COMPATIBLE',
      errors: [],
      ...details,
    };
  }

  if (compatibilityState === 'incompatible') {
    return {
      state: SHOPPER_STATES.NO_MATCH,
      reason: 'EXACT_EVIDENCED_INCOMPATIBLE',
      errors: [],
      ...details,
    };
  }

  if (compatibilityState === 'conditional') {
    return unknown('CONDITIONAL_REQUIRES_STRUCTURED_EVALUATOR', [], details);
  }

  return unknown('COMPATIBILITY_STATE_UNRESOLVED', [], details);
}

export function purchaseEnabled(result, confirmation, context = {}) {
  return (
    result?.state === SHOPPER_STATES.MATCH &&
    confirmation === true &&
    isConcrete(result?.ruleId) &&
    result?.contractVersion === ADAPTER_CONTRACT_VERSION &&
    isConcrete(result?.shopifyVariantId) &&
    result?.policyBlock !== true &&
    result?.allOtherPurchaseGatesPass === true &&
    result?.variantAvailable === true &&
    context.nativeVariantStatus === 'available' &&
    context.serverValidationReady === true &&
    context.connectivityBlocked !== true &&
    !networkOffline() &&
    ALLOWED_COUNTRIES.includes(text(context.countryCode).toUpperCase())
  );
}

export function buildCartGateProperties(result, confirmation, context = {}) {
  if (!purchaseEnabled(result, confirmation, context)) return null;

  return {
    _er_compatibility_state: SHOPPER_STATES.MATCH,
    _er_compatibility_confirmed: 'true',
    _er_compatibility_rule_id: text(result.ruleId),
    _er_contract_version: text(result.contractVersion),
    _er_truth_contract_version: text(result.truthContractVersion),
    _er_dataset_sha256: text(result.datasetSha256),
    _er_adapter_expires_at: text(result.adapterExpiresAt),
    _er_shopify_product_id: text(result.shopifyProductId),
    _er_shopify_variant_id: text(result.shopifyVariantId),
    _er_price_minor: text(result.approvedPriceMinor),
    _er_currency_code: text(result.currencyCode),
    _er_country_code: text(context.countryCode).toUpperCase(),
    _er_policy_block: 'false',
  };
}

export function validateCartGateProperties(properties, context = {}) {
  if (!properties || typeof properties !== 'object') return false;

  return (
    properties._er_compatibility_state === SHOPPER_STATES.MATCH &&
    properties._er_compatibility_confirmed === 'true' &&
    isConcrete(properties._er_compatibility_rule_id) &&
    properties._er_contract_version === ADAPTER_CONTRACT_VERSION &&
    properties._er_truth_contract_version === PRODUCT_TRUTH_CONTRACT_VERSION &&
    SHA256_PATTERN.test(text(properties._er_dataset_sha256)) &&
    parseIsoInstant(properties._er_adapter_expires_at) !== null &&
    parseIsoInstant(properties._er_adapter_expires_at) > Date.now() &&
    text(properties._er_shopify_product_id) === text(context.shopifyProductId) &&
    text(properties._er_shopify_variant_id) === text(context.shopifyVariantId) &&
    text(properties._er_price_minor) === text(context.shopifyPriceMinor) &&
    properties._er_currency_code === text(context.currencyCode).toUpperCase() &&
    ALLOWED_COUNTRIES.includes(properties._er_country_code) &&
    properties._er_policy_block === 'false'
  );
}

export function parseAdapterContract(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_error) {
    return null;
  }
}

function formatTemplate(template, values) {
  let output = text(template);
  for (const [key, value] of Object.entries(values)) {
    output = output.replaceAll(`%{${key}}`, text(value) || '—');
  }
  return output;
}

function networkOffline() {
  return typeof navigator === 'object' && navigator.onLine === false;
}

export function selectedOptionDisplayLabel(control, expectedValue) {
  const value = text(expectedValue);
  if (!value || control?.value !== value) return '';
  const options = Array.from(control.options || []).filter((option) => option.value === value);
  if (options.length !== 1 || options[0].selected !== true || options[0].disabled === true) return '';
  return typeof options[0].textContent === 'string' ? text(options[0].textContent) : '';
}

export function selectedVariantDisplayLabel(gate, currentInput) {
  const host = gate.closest?.('product-info');
  const variantId = text(gate.variantInput?.value);
  const productTitle = typeof gate.dataset.productTitle === 'string' ? text(gate.dataset.productTitle) : '';
  if (
    !variantId ||
    !productTitle ||
    !text(currentInput.shopifyProductId) ||
    !text(gate.dataset.sectionId) ||
    variantId !== text(currentInput.shopifyVariantId) ||
    text(host?.dataset?.productId) !== text(currentInput.shopifyProductId) ||
    text(host?.dataset?.section) !== text(gate.dataset.sectionId)
  ) return '';

  const allPickers = Array.from(host?.querySelectorAll?.('variant-selects') || []);
  if (allPickers.length === 0) {
    return variantId === text(gate.dataset.defaultVariantDisplayId) ? productTitle : '';
  }
  const pickers = allPickers.filter((picker) => picker.style?.display !== 'none');
  if (pickers.length !== 1 || text(pickers[0].dataset.section) !== text(gate.dataset.sectionId)) return '';

  const records = Array.from(pickers[0].querySelectorAll('script[data-selected-variant]'));
  if (records.length !== 1) return '';
  const variant = parseAdapterContract(records[0].textContent);
  if (
    !variant ||
    typeof variant !== 'object' ||
    Array.isArray(variant) ||
    !['string', 'number'].includes(typeof variant.id) ||
    (typeof variant.id === 'number' && !Number.isSafeInteger(variant.id)) ||
    text(variant.id) !== variantId ||
    typeof variant.title !== 'string' ||
    !text(variant.title) ||
    !Array.isArray(variant.options) ||
    variant.options.length < 1 ||
    variant.options.length > 3 ||
    variant.options.some((value) => typeof value !== 'string' || !text(value))
  ) return '';

  const groups = Array.from(pickers[0].querySelectorAll('.product-form__input'));
  if (groups.length !== variant.options.length) return '';
  for (let index = 0; index < groups.length; index += 1) {
    const controls = Array.from(groups[index].querySelectorAll('select, input[type="radio"]:checked'));
    if (controls.length !== 1 || controls[0].value !== variant.options[index]) return '';
  }
  return `${productTitle} · ${variant.options.join(' / ')}`;
}

const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

export class EarbudsRestoreOfflineStatus extends HTMLElementBase {
  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    this.handleNetworkChange = () => this.render();

    if (typeof window === 'object') {
      window.addEventListener('online', this.handleNetworkChange, {
        signal: this.abortController.signal,
      });
      window.addEventListener('offline', this.handleNetworkChange, {
        signal: this.abortController.signal,
      });
    }

    this.render();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    this.handleNetworkChange = null;
  }

  render() {
    const offline = networkOffline();
    this.hidden = !offline;
    this.dataset.networkStatus = offline ? 'offline' : 'online';

    const announcementLiveRegion = this.parentElement?.querySelector?.('[data-er-announcement-live]');
    announcementLiveRegion?.setAttribute('aria-live', offline ? 'off' : 'polite');
  }
}

export class EarbudsRestoreCompatibilityGate extends HTMLElementBase {
  evaluationContext() {
    return {};
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.abortController = new AbortController();
    const listenerOptions = { signal: this.abortController.signal };
    this.offline = networkOffline();
    this.dataset.networkStatus = this.offline ? 'offline' : 'online';
    this.handleNetworkChange = () => {
      this.offline = networkOffline();
      this.dataset.networkStatus = this.offline ? 'offline' : 'online';
      this.resetConfirmation();
      this.render();
    };

    if (typeof window === 'object') {
      window.addEventListener('online', this.handleNetworkChange, listenerOptions);
      window.addEventListener('offline', this.handleNetworkChange, listenerOptions);
    }

    this.contract = parseAdapterContract(this.dataset.contract);
    this.form = this.closest('form');
    this.partInput = this.querySelector('[data-er-part]');
    this.modelInput = this.querySelector('[data-er-model]');
    this.generationInput = this.querySelector('[data-er-generation]');
    this.sideInput = this.querySelector('[data-er-side]');
    this.confirmationInput = this.querySelector('[data-er-confirmation]');
    this.confirmationLabel = this.querySelector('[data-er-confirmation-label]');
    this.resultOutput = this.querySelector('[data-er-result]');
    this.submitButton = this.form?.querySelector('[data-er-gated-submit]') || null;
    this.variantInput = this.form?.querySelector('.product-variant-id') || null;
    this.nativeVariantStatus = this.submitButton?.dataset.erNativeStatus || 'unavailable';

    const materialSelectionInputs = this.querySelectorAll?.(MATERIAL_SELECTION_SELECTOR) || [];
    for (const input of new Set([
      this.partInput,
      this.modelInput,
      this.generationInput,
      this.sideInput,
      ...materialSelectionInputs,
    ])) {
      input?.addEventListener('input', () => this.handleCriteriaChange(), listenerOptions);
      input?.addEventListener('change', () => this.handleCriteriaChange(), listenerOptions);
    }

    this.variantInput?.addEventListener('change', () => this.handleCriteriaChange(), listenerOptions);
    this.confirmationInput?.addEventListener('change', () => this.render(), listenerOptions);
    this.form?.addEventListener('submit', (event) => this.guardSubmit(event), {
      capture: true,
      signal: this.abortController.signal,
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      if (PUB_SUB_EVENTS.optionValueSelectionChange) {
        this.unsubscribeOptionValueSelectionChange = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          ({ data } = {}) => {
            const variantPicker = data?.event?.target?.closest?.('variant-selects');
            if (text(variantPicker?.dataset?.section) !== text(this.dataset.sectionId)) return;
            this.handleCriteriaChange();
          },
        );
      }

      this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
        if (text(data?.sectionId) !== text(this.dataset.sectionId)) return;
        const fetchedSubmit = data?.html?.getElementById?.(`ProductSubmitButton-${data.sectionId}`);
        const fetchedGate = data?.html?.querySelector?.(
          `er-compatibility-gate[data-section-id="${data.sectionId}"]`,
        );
        this.nativeVariantStatus = fetchedSubmit?.dataset.erNativeStatus || 'unavailable';
        if (fetchedGate?.dataset.selectedVariantPrice) {
          this.dataset.selectedVariantPrice = fetchedGate.dataset.selectedVariantPrice;
        } else if (data?.variant?.price !== undefined) {
          this.dataset.selectedVariantPrice = text(data.variant.price);
        }
        if (fetchedGate?.dataset.contract && fetchedGate.dataset.contract !== this.dataset.contract) {
          this.dataset.contract = fetchedGate.dataset.contract;
          this.contract = parseAdapterContract(fetchedGate.dataset.contract);
        }
        this.handleCriteriaChange();
      });
    }

    if (typeof document === 'object') {
      this.handleLocalizationChange = (event) => {
        const localizationForm = event?.target?.closest?.('localization-form');
        const localizationChoice = event?.target?.closest?.('localization-form [data-value]');
        if (event?.type === 'submit' ? localizationForm : localizationChoice) this.handleCriteriaChange();
      };
      document.addEventListener('click', this.handleLocalizationChange, {
        capture: true,
        signal: this.abortController.signal,
      });
      document.addEventListener('submit', this.handleLocalizationChange, {
        capture: true,
        signal: this.abortController.signal,
      });
    }

    if (typeof MutationObserver === 'function') {
      this.contextObserver = new MutationObserver((records) => {
        if (!records.some(({ attributeName }) => MATERIAL_CONTEXT_ATTRIBUTES.includes(attributeName))) return;
        if (records.some(({ attributeName }) => attributeName === 'data-contract')) {
          this.contract = parseAdapterContract(this.dataset.contract);
        }
        this.handleCriteriaChange();
      });
      this.contextObserver.observe(this, {
        attributes: true,
        attributeFilter: MATERIAL_CONTEXT_ATTRIBUTES,
      });
    }
    this.render();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.contextObserver?.disconnect();
    this.contextObserver = null;
    this.unsubscribeOptionValueSelectionChange?.();
    this.unsubscribeOptionValueSelectionChange = null;
    this.unsubscribeVariantChange?.();
    this.unsubscribeVariantChange = null;
    this.handleNetworkChange = null;
    this.abortController = null;
    this.initialized = false;
  }

  resetConfirmation() {
    if (this.confirmationInput) this.confirmationInput.checked = false;
    this.dataset.purchaseEnabled = 'false';
    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
    }
  }

  handleCriteriaChange() {
    this.resetConfirmation();
    this.render();
  }

  input() {
    return {
      shopifyProductId: this.dataset.productId,
      shopifyVariantId: this.variantInput?.value || this.dataset.selectedVariantId,
      shopifyPriceMinor: this.dataset.selectedVariantPrice,
      currencyCode: this.dataset.currencyCode,
      requestedPartType: this.partInput?.value,
      targetModelCode: this.modelInput?.value,
      targetGenerationCode: this.generationInput?.value,
      targetSide: this.sideInput?.value,
    };
  }

  currentResult(currentInput = this.input()) {
    if (this.offline === true || networkOffline()) {
      return unknown('NETWORK_OFFLINE');
    }
    return evaluateCompatibility(this.contract, currentInput, this.evaluationContext());
  }

  render() {
    const currentInput = this.input();
    const result = this.currentResult(currentInput);
    const context = {
      countryCode: this.dataset.countryCode,
      nativeVariantStatus: this.nativeVariantStatus,
      serverValidationReady: false,
      connectivityBlocked: this.offline === true || networkOffline(),
    };
    const confirmed = this.confirmationInput?.checked === true;
    const enabled = purchaseEnabled(result, confirmed, context);
    const properties = buildCartGateProperties(result, confirmed, context);

    this.dataset.state = result.state;
    this.dataset.purchaseEnabled = enabled ? 'true' : 'false';

    if (this.resultOutput) {
      this.resultOutput.setAttribute('aria-live', context.connectivityBlocked ? 'off' : 'polite');
      const labels = {
        MATCH: this.dataset.labelMatch,
        NO_MATCH: this.dataset.labelNoMatch,
        UNKNOWN: this.dataset.labelUnknown,
      };
      this.resultOutput.textContent = labels[result.state] || this.dataset.labelUnknown || SHOPPER_STATES.UNKNOWN;
    }

    if (this.confirmationInput) {
      this.confirmationInput.disabled =
        result.state !== SHOPPER_STATES.MATCH ||
        result.policyBlock === true ||
        result.allOtherPurchaseGatesPass !== true ||
        result.variantAvailable !== true ||
        this.nativeVariantStatus !== 'available' ||
        !ALLOWED_COUNTRIES.includes(text(this.dataset.countryCode).toUpperCase());
      if (this.confirmationInput.disabled) this.confirmationInput.checked = false;
    }

    if (this.confirmationLabel) {
      const displayValues = {
        part: selectedOptionDisplayLabel(this.partInput, currentInput.requestedPartType),
        model: currentInput.targetModelCode,
        generation: currentInput.targetGenerationCode,
        side: selectedOptionDisplayLabel(this.sideInput, currentInput.targetSide),
        unit: selectedVariantDisplayLabel(this, currentInput),
      };
      this.confirmationLabel.textContent = text(this.dataset.confirmationTemplate).replace(
        /%\{(part|model|generation|side|unit)\}/g,
        (_placeholder, key) => text(displayValues[key]) || '—',
      );
    }

    if (this.submitButton) {
      this.submitButton.disabled = !enabled;
      this.submitButton.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      const submitLabel = this.submitButton.querySelector('[data-er-submit-label]');
      if (submitLabel) {
        submitLabel.textContent =
          this.nativeVariantStatus === 'sold_out'
            ? this.submitButton.dataset.erSoldOutLabel
            : this.nativeVariantStatus === 'unavailable'
              ? this.submitButton.dataset.erUnavailableLabel
              : this.submitButton.dataset.erAddLabel;
      }
    }

    for (const input of this.querySelectorAll('[data-er-cart-property]')) {
      const key = input.dataset.erCartProperty;
      input.value = properties?.[key] || (key === '_er_compatibility_state' ? result.state : '');
    }

    this.dispatchEvent(
      new CustomEvent('er:compatibility-state', {
        bubbles: true,
        detail: { state: result.state, purchaseEnabled: enabled },
      }),
    );
  }

  guardSubmit(event) {
    const result = this.currentResult();
    const enabled = purchaseEnabled(result, this.confirmationInput?.checked === true, {
      countryCode: this.dataset.countryCode,
      nativeVariantStatus: this.nativeVariantStatus,
      serverValidationReady: false,
      connectivityBlocked: this.offline === true || networkOffline(),
    });

    if (!enabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.render();
      this.resultOutput?.focus();
    }
  }
}

if (typeof window !== 'undefined' && 'customElements' in window) {
  if (!window.customElements.get('er-offline-status')) {
    window.customElements.define('er-offline-status', EarbudsRestoreOfflineStatus);
  }
  if (!window.customElements.get('er-compatibility-gate')) {
    window.customElements.define('er-compatibility-gate', EarbudsRestoreCompatibilityGate);
  }
}
